(() => {
  if (window.iGardenPoolQuizFeedback) return;

  const TURNSTILE_SCRIPT_ID = 'igarden-turnstile-api';
  const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  let turnstileLoader;

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileLoader) return turnstileLoader;

    turnstileLoader = new Promise((resolve, reject) => {
      const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
      const script = existingScript || document.createElement('script');
      const timeoutId = window.setTimeout(() => {
        turnstileLoader = undefined;
        reject(new Error('Turnstile API timed out while loading.'));
      }, 15000);

      const handleLoad = () => {
        window.clearTimeout(timeoutId);
        if (window.turnstile) resolve(window.turnstile);
        else {
          turnstileLoader = undefined;
          reject(new Error('Turnstile API did not initialize.'));
        }
      };
      const handleError = () => {
        window.clearTimeout(timeoutId);
        turnstileLoader = undefined;
        reject(new Error('Unable to load Turnstile.'));
      };

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });

      if (!existingScript) {
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        document.head.append(script);
      }
    });

    return turnstileLoader;
  }

  async function getTurnstileToken(siteKey) {
    const turnstile = await loadTurnstile();
    const container = document.createElement('div');
    container.setAttribute('aria-hidden', 'true');
    Object.assign(container.style, {
      position: 'fixed',
      width: '1px',
      height: '1px',
      left: '-10000px',
      bottom: '0',
      overflow: 'hidden'
    });
    document.body.append(container);

    return new Promise((resolve, reject) => {
      let widgetId;
      let settled = false;
      let retryCount = 0;
      const timeoutId = window.setTimeout(() => finish(new Error('Turnstile timed out.')), 30000);

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        if (widgetId !== undefined) {
          try {
            turnstile.remove(widgetId);
          } catch {
            // The widget may already have removed itself after an error.
          }
        }
        container.remove();
      };

      const finish = (error, token) => {
        if (settled) return;
        settled = true;
        cleanup();
        if (error) reject(error);
        else resolve(token);
      };

      try {
        widgetId = turnstile.render(container, {
          sitekey: siteKey,
          size: 'invisible',
          execution: 'execute',
          appearance: 'execute',
          retry: 'auto',
          'retry-interval': 1500,
          callback: (token) => window.setTimeout(() => finish(null, token), 0),
          'error-callback': (errorCode) => {
            const code = String(errorCode || 'unknown');
            const retryable = code.startsWith('300') || code.startsWith('600') || code === '200500';

            if (retryable && retryCount < 2) {
              retryCount += 1;
              return false;
            }

            window.setTimeout(
              () => finish(new Error(`Turnstile verification failed (${code}).`)),
              0
            );
            return true;
          },
          'expired-callback': () => window.setTimeout(
            () => finish(new Error('Turnstile token expired.')),
            0
          ),
          'timeout-callback': () => window.setTimeout(
            () => finish(new Error('Turnstile challenge timed out.')),
            0
          )
        });
        turnstile.execute(widgetId);
      } catch (error) {
        finish(error);
      }
    });
  }

  window.iGardenPoolQuizFeedback = async (payload) => {
    const quiz = document.getElementById(`PoolCleanerQuiz-${payload.sectionId}`);
    if (!quiz || quiz.dataset.feedbackEnabled !== 'true') return { skipped: true };

    const endpoint = String(quiz.dataset.feedbackEndpoint || '').trim();
    const siteKey = String(quiz.dataset.turnstileSiteKey || '').trim();
    if (!endpoint || !siteKey) throw new Error('Feedback upload is not configured.');

    const turnstileToken = await getTurnstileToken(siteKey);
    const response = await fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, turnstileToken })
    });

    let result = {};
    try {
      result = await response.json();
    } catch {
      // A non-JSON error response is handled by the status check below.
    }

    if (!response.ok) throw new Error(result.error || 'Feedback request failed.');
    return result;
  };
})();
